/**
 * The bar above the library table, in the `ListPage` shape: the
 * Library / Skills segment, search and the active filter pills, then Filter,
 * Display (ordering) and refresh on the right. Filters are the library's
 * own: kind, visa, draft type, status, client details, format and tag; they
 * apply inside any folder. Search spans every folder unless the In: chip
 * narrows it to the open one, and below 1100px a folder picker stands in for
 * the rail.
 */
import type { FC, MouseEvent, PointerEvent, ReactNode } from "react";
import {
  Activity,
  Check,
  File,
  FileText,
  Flag,
  Inbox,
  Layers,
  ListFilter,
  RefreshCw,
  Shield,
  Tag,
  X,
  type LucideIcon
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { DisplayOptions } from "@/components/ui/display-options";
import { FilterChip } from "@/components/ui/filter-chip";
import { ListSearch } from "@/components/ui/list-toolbar";

import { ALL_DOCUMENTS, UNFILED, type FolderTree } from "../folders";
import {
  CATEGORIES,
  CATEGORY_LABEL,
  facetValue,
  LIBRARY_SORT_OPTIONS,
  setFacet,
  STATUS_FACET_LABEL,
  type LibraryFacetKey,
  type LibraryFilter,
  type LibrarySort,
  type LibraryStatusFacet
} from "../model";
import { FolderSelect, type FolderSelectChoice } from "./FolderSelect";

const FOLDER_SCOPE_CHOICES: FolderSelectChoice[] = [
  { value: ALL_DOCUMENTS, label: "All Documents", icon: Layers },
  { value: UNFILED, label: "Unfiled", icon: Inbox }
];

export type FacetOption = { value: string; label: string };

type FacetDefinition = {
  key: LibraryFacetKey;
  label: string;
  icon: LucideIcon;
  allLabel: string;
  options: FacetOption[];
  emptyLabel: string;
};

const FORMAT_OPTIONS: FacetOption[] = [
  { value: "docx", label: "Word" },
  { value: "pdf", label: "PDF" },
  { value: "other", label: "Other" }
];

const KIND_OPTIONS: FacetOption[] = CATEGORIES.map((value) => ({
  value,
  label: CATEGORY_LABEL[value]
}));

const STATUS_OPTIONS: FacetOption[] = (
  Object.keys(STATUS_FACET_LABEL) as LibraryStatusFacet[]
).map((value) => ({ value, label: STATUS_FACET_LABEL[value] }));

export type FacetChoices = {
  tags: string[];
  visas: string[];
  draftTypes: FacetOption[];
};

const facetDefinitions = (choices: FacetChoices): FacetDefinition[] => [
  {
    key: "kind",
    label: "Kind",
    icon: Layers,
    allLabel: "All Kinds",
    options: KIND_OPTIONS,
    emptyLabel: ""
  },
  {
    key: "visa",
    label: "Visa",
    icon: Flag,
    allLabel: "All Visas",
    options: choices.visas.map((v) => ({ value: v, label: v })),
    emptyLabel: "No document names a visa yet"
  },
  {
    key: "draftType",
    label: "Draft Type",
    icon: FileText,
    allLabel: "All Draft Types",
    options: choices.draftTypes,
    emptyLabel: "No document names a draft type yet"
  },
  {
    key: "status",
    label: "Status",
    icon: Activity,
    allLabel: "Any Status",
    options: STATUS_OPTIONS,
    emptyLabel: ""
  },
  {
    key: "clientDetails",
    label: "Client Details",
    icon: Shield,
    allLabel: "Any",
    options: [{ value: "found", label: "Contains Client Details" }],
    emptyLabel: ""
  },
  {
    key: "format",
    label: "Format",
    icon: File,
    allLabel: "All Formats",
    options: FORMAT_OPTIONS,
    emptyLabel: ""
  },
  {
    key: "tag",
    label: "Tag",
    icon: Tag,
    allLabel: "All Tags",
    options: choices.tags.map((t) => ({ value: t, label: t })),
    emptyLabel: "No tags yet"
  }
];

const MenuCheck: FC<{ on: boolean }> = ({ on }) =>
  on ? <Check className="ml-auto size-3.5 text-accent-seed" /> : null;

const FacetPill: FC<{
  facet: FacetDefinition;
  value: string | null;
  onChange: (value: string | null) => void;
  onRemove: () => void;
}> = ({ facet, value, onChange, onRemove }) => {
  const current =
    value === null
      ? "All"
      : facet.options.find((o) => o.value === value)?.label ?? value;
  const Icon = facet.icon;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterChip
          active
          className="max-w-full min-w-0"
          aria-label={`Filter by ${facet.label.toLowerCase()}`}
          data-testid={`facet-${facet.key}`}
        >
          <Icon />
          <span className="truncate">{facet.label}:</span>
          <span className="max-w-40 min-w-0 truncate text-fg">{current}</span>
          <span
            role="button"
            tabIndex={-1}
            className="-mr-1 flex size-4 shrink-0 items-center justify-center rounded-full text-fg-3 hover:bg-fill-2 hover:text-fg"
            aria-label={`Remove the ${facet.label.toLowerCase()} filter`}
            onPointerDown={(e: PointerEvent) => e.stopPropagation()}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              onRemove();
            }}
          >
            <X className="size-3!" aria-hidden />
          </span>
        </FilterChip>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-auto max-w-90 min-w-60">
        <DropdownMenuItem onSelect={() => onChange(null)}>
          <span className="min-w-0 flex-1 truncate">{facet.allLabel}</span>
          <MenuCheck on={value === null} />
        </DropdownMenuItem>
        {facet.options.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <div className="max-h-70 min-w-0 overflow-y-auto">
              {facet.options.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={() =>
                    onChange(value === option.value ? null : option.value)
                  }
                >
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                  <MenuCheck on={value === option.value} />
                </DropdownMenuItem>
              ))}
            </div>
          </>
        ) : (
          <div className="px-2 py-2 text-sm text-fg-3">{facet.emptyLabel}</div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const sortKey = (sort: LibrarySort) =>
  `${sort.sortBy || "default"}-${sort.sortDir}`;

type Props = {
  /** The Library / Skills segment, first in the row. */
  leading?: ReactNode;
  filter: LibraryFilter;
  onFilterChange: (filter: LibraryFilter) => void;
  /** Pills on screen: every set facet plus any just added and not yet chosen. */
  shownFacets: LibraryFacetKey[];
  onShownFacetsChange: (keys: LibraryFacetKey[]) => void;
  choices: FacetChoices;
  sort: LibrarySort;
  onSortChange: (sort: LibrarySort) => void;
  onRefresh: () => void;
  /** The open folder, for the picker that stands in for the rail below 1100px. */
  folderScope?: {
    tree: FolderTree;
    value: string;
    onChange: (scope: string) => void;
  };
  /** While searching inside a folder: the In: chip that narrows the search to it. */
  scopeChip?: ReactNode;
};

export const LibraryTableToolbar: FC<Props> = ({
  leading,
  filter,
  onFilterChange,
  shownFacets,
  onShownFacetsChange,
  choices,
  sort,
  onSortChange,
  onRefresh,
  folderScope,
  scopeChip
}) => {
  const facets = facetDefinitions(choices);
  const facetsToAdd = facets.filter((f) => !shownFacets.includes(f.key));
  const shown = shownFacets
    .map((key) => facets.find((f) => f.key === key))
    .filter((f): f is FacetDefinition => Boolean(f));

  const removeFacet = (key: LibraryFacetKey) => {
    onShownFacetsChange(shownFacets.filter((k) => k !== key));
    onFilterChange(setFacet(filter, key, null));
  };

  return (
    <div
      className="flex w-full flex-none items-center gap-2 border-b border-line-2 bg-surface px-6 pb-3"
      data-testid="library-toolbar"
    >
      {leading}
      {folderScope ? (
        <div className="flex max-w-56 min-w-0 shrink min-[1100px]:hidden">
          <FolderSelect
            tree={folderScope.tree}
            value={folderScope.value}
            onChange={folderScope.onChange}
            choices={FOLDER_SCOPE_CHOICES}
            ariaLabel="Open a Folder"
            className="w-full"
            data-testid="library-folder-picker"
          />
        </div>
      ) : null}
      <ListSearch
        className="w-50 shrink"
        placeholder="Search documents…"
        aria-label="Search the library"
        value={filter.query}
        onValueChange={(query) => onFilterChange({ ...filter, query })}
      />

      {/* Horizontally scrollable active filter pills */}
      <div className="relative min-w-0 flex-1 overflow-hidden">
        <div className="flex scrollbar-none items-center gap-2 overflow-x-auto px-1 [&::-webkit-scrollbar]:hidden">
          {scopeChip}
          {shown.map((facet) => (
            <FacetPill
              key={facet.key}
              facet={facet}
              value={facetValue(filter, facet.key)}
              onChange={(value) =>
                onFilterChange(setFacet(filter, facet.key, value))
              }
              onRemove={() => removeFacet(facet.key)}
            />
          ))}
        </div>
        {/* Right-edge fade mask */}
        <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l from-surface to-transparent" />
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {facetsToAdd.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" aria-label="Add filter">
                <ListFilter />
                <span className="hidden md:inline">Filter</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-auto min-w-50">
              {facetsToAdd.map((f) => {
                const Icon = f.icon;
                return (
                  <DropdownMenuItem
                    key={f.key}
                    onSelect={() =>
                      onShownFacetsChange([...shownFacets, f.key])
                    }
                  >
                    <Icon className="size-3.5 text-fg-3" />
                    <span className="min-w-0 flex-1 truncate">{f.label}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <DisplayOptions
          ordering={{
            options: LIBRARY_SORT_OPTIONS.map(({ value, label }) => ({
              value: sortKey(value),
              label
            })),
            value: sortKey(sort),
            onValueChange: (key) => {
              const picked = LIBRARY_SORT_OPTIONS.find(
                (o) => sortKey(o.value) === key
              );
              if (picked) onSortChange(picked.value);
            }
          }}
        />
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Refresh"
          onClick={onRefresh}
        >
          <RefreshCw />
        </Button>
      </div>
    </div>
  );
};
